import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvCompactButton from '../atoms/OvCompactButton';

interface ApiKeyCreationModalProps {
  open: boolean;
  onClose: () => void;
  apiKey: string;
  partnerName: string;
}

const ApiKeyCreationModal: React.FC<ApiKeyCreationModalProps> = ({
  open,
  onClose,
  apiKey,
  partnerName,
}) => {
  const { t } = useTranslation();
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy API key:', error);
    }
  };

  const toggleVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: Colours.OV_WHITE,
        },
      }}
    >
      <StyledDialogTitle>{t('apiKeys.creation.title')}</StyledDialogTitle>

      <DialogContent>
        <StyledWarningAlert>
          {t('apiKeys.creation.securityWarning')}
        </StyledWarningAlert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {t('apiKeys.creation.description', { partnerName })}
          </Typography>
        </Box>

        <ApiKeyContainer>
          <TextField
            fullWidth
            label={t('apiKeys.fields.apiKey')}
            value={showApiKey ? apiKey : '•'.repeat(apiKey.length)}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip
                    title={
                      showApiKey
                        ? t('common.actions.hide')
                        : t('common.actions.show')
                    }
                  >
                    <IconButton onClick={toggleVisibility} size="small">
                      {showApiKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      copied
                        ? t('common.actions.copied')
                        : t('common.actions.copy')
                    }
                  >
                    <IconButton onClick={handleCopy} size="small">
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '14px',
              },
            }}
          />
        </ApiKeyContainer>

        <StyledInfoAlert>{t('apiKeys.creation.storageNote')}</StyledInfoAlert>
      </DialogContent>

      <StyledDialogActions>
        <OvCompactButton onClick={onClose}>
          {t('common.actions.understood')}
        </OvCompactButton>
      </StyledDialogActions>
    </Dialog>
  );
};

const StyledDialogTitle = styled(DialogTitle)`
  && {
    text-align: center;
    font-weight: bold;
    font-size: ${Variables.fontSizes.XLARGE};
    color: ${Colours.OV_CHARCOAL};
    padding: 1.5rem 1.5rem 0.5rem 1.5rem;
  }
`;

const StyledWarningAlert = styled.div`
  background-color: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: ${Variables.borderRadius.SMALL};
  padding: 1rem;
  margin-bottom: 1rem;
  color: #e65100;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledInfoAlert = styled.div`
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: ${Variables.borderRadius.SMALL};
  padding: 1rem;
  margin-top: 1rem;
  color: ${Colours.OV_BASE};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledDialogActions = styled(DialogActions)`
  && {
    padding: 0 1.5rem 1.5rem 1.5rem;
    justify-content: flex-end;
  }
`;

const ApiKeyContainer = styled(Box)`
  background-color: ${Colours.OV_SEMI_LIGHT};
  border-radius: ${Variables.borderRadius.SMALL};
  padding: 1rem;
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

export default ApiKeyCreationModal;
